import {
  computed,
  type InjectionKey,
  type MaybeRefOrGetter,
  nextTick,
  onScopeDispose,
  type Ref,
  ref,
  toValue,
  watch,
} from "vue"
import type { Field } from "@auger/core/fields"
import type { Toast } from "@auger/ui/composables/types"
import type {
  GroupedItem,
  GroupKey,
  SortKey,
  useCollection,
} from "@auger/ui/composables/useCollection"
import type { ViewSettingEntry } from "@auger/ui/composables/viewSettings/entry"
import {
  groupKeyList,
  isObject,
  numberRecord,
  sortKeyList,
  stringList,
} from "@auger/ui/composables/viewSettings/parse"
import { useViewSettings } from "@auger/ui/composables/viewSettings/useViewSettings"
import { useIsMobile } from "./useMediaQuery"

// A useGrid column = Field plus grid-only presentation metadata. On input the
// metadata is optional; enhancedColumns attaches it and returns EnhancedColumn
// where it's guaranteed.
export interface Column extends Field {
  width?: number
  flex?: string
  isFrozen?: boolean
  frozenLeft?: string | null
}

// Output of enhancedColumns: every presentation field is populated.
export interface EnhancedColumn extends Column {
  width: number
  flex: string
  isFrozen: boolean
  frozenLeft: string | null
}

// Persistence shape stored in the view settings under `grid:${settingsKey}`. Loose
// on read (any of these can be missing or corrupt — loadSettings tolerates
// every absence), strict on write (see PersistedGridSettings).
export interface GridSettings {
  columnKeys?: string[]
  columnWidths?: Record<string, number>
  rowHeight?: number
  scrollTop?: number
  scrollLeft?: number
  sortKeys?: SortKey[]
  groupKeys?: GroupKey[]
  expandedGroups?: string[]
}

// The write path populates every field; Required<> guarantees no field is
// silently dropped from a persisted payload.
export type PersistedGridSettings = Required<GridSettings>

const isNumber = (value: unknown): value is number => typeof value === "number"

// The parts of a stored payload that check out; anything else is left to the defaults.
function parseGridSettings(stored: unknown): GridSettings | null {
  if (!isObject(stored)) return null
  return {
    columnKeys: stringList(stored.columnKeys),
    columnWidths: numberRecord(stored.columnWidths),
    rowHeight: isNumber(stored.rowHeight) ? stored.rowHeight : undefined,
    scrollTop: isNumber(stored.scrollTop) ? stored.scrollTop : undefined,
    scrollLeft: isNumber(stored.scrollLeft) ? stored.scrollLeft : undefined,
    sortKeys: sortKeyList(stored.sortKeys),
    groupKeys: groupKeyList(stored.groupKeys),
    expandedGroups: stringList(stored.expandedGroups),
  }
}

// Settings that stay with the user rather than a list's shared view.
const PERSONAL_KEYS = ["scrollTop", "scrollLeft", "expandedGroups"]

// A row in the virtual-scroll list — either a group header or a data row
// carrying the source item, exactly as useCollection produces them. Grid
// templates read `.item` for the row's data; no alias on top.
export type GridRow<T> = GroupedItem<T>

/**
 * Rendered height of a group header row. The virtual-scroll math below and
 * `GridGroupRow`'s CSS must agree on this to the pixel — they disagreed (32 vs
 * 36), which under-reported `totalHeight` and skewed `visibleItemsOffset` by
 * 4px per group above the viewport, so a grouped grid drifted out of step with
 * its own scrollbar. GridElement publishes this as `--group-header-height` and
 * GridGroupRow reads it, so the two can't part company again.
 */
export const GROUP_HEADER_HEIGHT = 36

/** The grid's row-height class (is-short-rows / is-medium-rows / is-tall-rows),
 *  for what renders outside `.grid-element` (a cell's teleported editor). */
export const GRID_ROWS_CLASS: InjectionKey<Ref<string>> = Symbol("grid-rows-class")

export interface UseGridOptions {
  settingsKey?: Ref<string | null | undefined>
  /** Whether the grid is on screen. Settings load while it is and stop saving
   *  while it isn't. Defaults to true; a parent switching layouts passes it. */
  active?: MaybeRefOrGetter<boolean>
  defaultRowHeight?: number
  groupHeaderHeight?: number
  /** Render the selection gutter (row number + checkbox) — grid chrome pinned
   *  sticky-left by GridRow/GridHeader. Defaults to true; pass false for grids
   *  with no row selection. The selection instance itself is passed to
   *  GridElement, not here; useGrid only needs to reserve the gutter column. */
  showRowSelect?: boolean
  freeze?: boolean
  overscan?: number
  toast?: Toast | null
  /** Columns shown until the user picks their own. Omitted, every visible field. */
  defaultColumnKeys?: string[] | null
  /** Which fields show until the user picks their own; omitted, every visible field. */
  defaultField?: (field: Field) => boolean
  /** A list's saved view, which wins over an older user entry — see useViewSettings. */
  listView?: Ref<ViewSettingEntry | null | undefined>
}

// A column's width when its field declares none.
export const DEFAULT_COLUMN_WIDTH = 200

// Width of the selection gutter. Frozen data columns offset their `frozenLeft`
// past it so they sit flush against it under horizontal scroll.
export const SELECT_GUTTER_WIDTH = 36

// Shape of pending-column-keys state while waiting for additional schema
// fields to load — see the `pendingColumnKeys` variable and the
// dataColumns watcher that retries reconstruction.
interface PendingColumnKeys {
  keys: string[]
  widths?: Record<string, number>
}

// DOM container surface the composable depends on. Picked narrowly so
// tests can supply a plain object instead of a real HTMLElement.
type GridContainer = Pick<HTMLElement, "scrollTop" | "scrollLeft" | "clientHeight" | "clientWidth">

// Module-scoped flag: show the save-failure toast at most once per session.
// Without dedup, a persistent error (offline, server rejecting the write) would
// toast on every state change since saveSettings is a debounced watcher
// triggered by column/sort/group changes.
let _saveErrorToastShown = false

/**
 * Composable for Grid presentation: columns, virtual scrolling, and settings
 * persistence. Requires a useCollection instance for data organization.
 *
 * @example
 * const collection = useCollection(items, { fields, getItemKey })
 * const grid = useGrid(collection, {
 *   settingsKey: computed(() => `list:${listId.value}`),
 *   showRowSelect: true, // reserve the selection gutter (grid chrome)
 *   toast,
 * })
 */
export function useGrid<T extends Record<string, unknown> = Record<string, unknown>>(
  // The item-bearing members are spelled out so `T` infers from the caller's
  // collection; TS can't infer it back through `ReturnType<typeof useCollection<T>>`.
  collection: Pick<
    ReturnType<typeof useCollection<T>>,
    | "allFields"
    | "expandedGroups"
    | "groupKeys"
    | "isGrouped"
    | "resetState"
    | "setSortedFields"
    | "sortKeys"
  > & {
    filteredItems: Ref<T[]>
    groupedItems: Ref<GroupedItem<T>[]>
  },
  options: UseGridOptions = {}
) {
  const {
    settingsKey,
    active = true,
    defaultRowHeight = 32,
    groupHeaderHeight = GROUP_HEADER_HEIGHT,
    showRowSelect = true,
    freeze = true,
    overscan = 10,
    toast = null,
    defaultColumnKeys = null,
    defaultField,
    listView,
  } = options

  // --- Grid-specific State ---
  const displayFields = ref<Column[]>([])
  const rowHeight = ref(defaultRowHeight)
  const scrollTop = ref(0)
  const containerHeightPx = ref(0)
  const isLoaded = ref(false)
  // Stored keys that couldn't be resolved when loadSettings ran because their
  // schema hadn't loaded yet — the dataColumns watcher retries.
  let pendingColumnKeys: PendingColumnKeys | null = null
  // Set by resetSettings() to skip the deep watcher's very next firing — see there.
  let suppressNextSave = false

  // Grid container ref (bound by the consuming component)
  const gridContainer = ref<GridContainer | null>(null)

  // --- Computed: Columns ---

  // Data columns shared across all presentation layers; the gutter lives outside
  // this list as grid chrome.
  const dataColumns = computed<Column[]>(() => {
    return collection.allFields.value.filter((col) => !col.isHidden) as Column[]
  })

  // The columns a grid opens with before any saved settings.
  function defaultColumns(): Column[] {
    const all = dataColumns.value
    if (defaultColumnKeys?.length) {
      const picked = defaultColumnKeys
        .map((key) => all.find((col) => col.key === key))
        .filter((col): col is Column => !!col)
      if (picked.length) return picked
    }
    if (defaultField) {
      const picked = all.filter(defaultField)
      if (picked.length) return picked
    }
    return [...all]
  }

  // A frozen first column is often most of a phone's width, leaving the other
  // columns no room to scroll into view — so phones don't freeze it.
  const isMobile = useIsMobile()

  // The first data column freezes; the gutter is separately sticky-left before it.
  const frozenColumnIndices = computed<number[]>(() => {
    if (!freeze || isMobile.value) return []
    return displayFields.value.length ? [0] : []
  })

  // Attaches frozen metadata, offsetting frozen columns past the gutter.
  const enhancedColumns = computed<EnhancedColumn[]>(() => {
    const cols = displayFields.value

    if (cols.length === 0) {
      // Before settings load, fall back to data columns with inert presentation
      // defaults so the output is uniformly EnhancedColumn.
      return dataColumns.value.map((col) => ({
        ...col,
        width: col.width ?? DEFAULT_COLUMN_WIDTH,
        flex: col.flex ?? "0 0 auto",
        isFrozen: col.isFrozen ?? false,
        frozenLeft: col.frozenLeft ?? null,
      }))
    }

    // Frozen columns stack after the gutter.
    let frozenLeft = showRowSelect ? SELECT_GUTTER_WIDTH : 0

    return cols.map((col, index) => {
      const isFrozen = frozenColumnIndices.value.includes(index)
      const column: EnhancedColumn = {
        ...col,
        width: col.width ?? DEFAULT_COLUMN_WIDTH,
        isFrozen,
        frozenLeft: isFrozen ? `${frozenLeft}px` : null,
        // The last column takes up the remaining width.
        flex: index === cols.length - 1 ? "1 1 auto" : col.flex || "0 0 auto",
      }
      if (isFrozen) frozenLeft += column.width
      return column
    })
  })

  // --- Computed: Virtual Scrolling ---

  /**
   * Rows to display (from collection, with grouping applied). Each entry is a
   * group header or a data row carrying its source item in `.item`.
   */
  const groupedRows = computed<GridRow<T>[]>(() => collection.groupedItems.value)

  // A row's rendered height. `dataHeight` lets the anchor math below measure
  // data rows under a height other than the current one.
  const heightOf = (row: GridRow<T>, dataHeight = rowHeight.value) =>
    row.type === "group" ? groupHeaderHeight : dataHeight

  const totalHeight = computed(() => {
    if (!collection.isGrouped.value) {
      return collection.filteredItems.value.length * rowHeight.value
    }
    return groupedRows.value.reduce((height, row) => height + heightOf(row), 0)
  })

  // Ungrouped rows share one height; grouped, header and data rows differ, so
  // the range walks them summing heights.
  const visibleRange = computed<{ start: number; end: number }>(() => {
    if (!collection.isGrouped.value) {
      const start = Math.floor(scrollTop.value / rowHeight.value)
      const visibleCount = Math.ceil(containerHeightPx.value / rowHeight.value)

      return {
        start: Math.max(0, start - overscan),
        end: Math.min(collection.filteredItems.value.length, start + visibleCount + overscan),
      }
    }

    const items = groupedRows.value
    let accumulatedHeight = 0
    let startIndex = 0
    let endIndex = items.length

    for (let i = 0; i < items.length; i++) {
      const itemHeight = heightOf(items[i]!)
      if (accumulatedHeight + itemHeight > scrollTop.value) {
        startIndex = Math.max(0, i - overscan)
        break
      }
      accumulatedHeight += itemHeight
    }

    // Re-sum from startIndex to find the end.
    accumulatedHeight = offsetForIndex(startIndex, rowHeight.value)
    const targetBottom = scrollTop.value + containerHeightPx.value
    for (let i = startIndex; i < items.length; i++) {
      accumulatedHeight += heightOf(items[i]!)
      if (accumulatedHeight > targetBottom) {
        endIndex = Math.min(items.length, i + overscan + 1)
        break
      }
    }

    return { start: startIndex, end: endIndex }
  })

  /**
   * For non-grouped data: array of row indices to render.
   */
  const visibleIndices = computed<number[]>(() => {
    if (collection.isGrouped.value) return []
    const { start, end } = visibleRange.value
    return Array.from({ length: end - start }, (_, i) => start + i)
  })

  /**
   * For grouped data: slice of groupedRows to render.
   */
  const visibleItems = computed<GridRow<T>[]>(() => {
    if (!collection.isGrouped.value) {
      return []
    }
    return groupedRows.value.slice(visibleRange.value.start, visibleRange.value.end)
  })

  /**
   * Y offset for the visible items container.
   */
  const visibleItemsOffset = computed(() =>
    offsetForIndex(visibleRange.value.start, rowHeight.value)
  )

  // --- Row height: keep the viewport anchored ------------------------------
  // scrollTop is in pixels, so changing row height without touching it silently
  // teleports the reader: Tall (192) → Short (32) at scrollTop 2000 moves them
  // from row ~10 to row ~62. Resolve the row at the viewport's top edge under
  // the OLD height, then re-derive its offset under the new one.

  /** Index of the row occupying `offset`, measured with `dataHeight` per data row. */
  function indexAtOffset(offset: number, dataHeight: number): number {
    if (!collection.isGrouped.value) return Math.floor(offset / dataHeight)
    let accumulated = 0
    const items = groupedRows.value
    for (let i = 0; i < items.length; i++) {
      accumulated += heightOf(items[i]!, dataHeight)
      if (accumulated > offset) return i
    }
    return items.length
  }

  /** Inverse of `indexAtOffset`: where row `index` starts at `dataHeight`. */
  function offsetForIndex(index: number, dataHeight: number): number {
    if (!collection.isGrouped.value) return index * dataHeight
    return groupedRows.value
      .slice(0, index)
      .reduce((offset, row) => offset + heightOf(row, dataHeight), 0)
  }

  // loadSettings/resetSettings write rowHeight as part of restoring a whole view
  // (and restore their own scroll position right after) — anchoring there would
  // fight them. Only a live change by the user should re-anchor.
  let suppressNextRowHeightAnchor = false

  // Restore a row height without re-anchoring. The flag is set only for a
  // real change: the watcher never fires on a no-op, so a flag set then would
  // swallow the user's next change instead.
  function restoreRowHeight(height: number) {
    if (rowHeight.value === height) return
    suppressNextRowHeightAnchor = true
    rowHeight.value = height
  }

  // `flush: "post"` so the rows have already taken the new height — the
  // container's scrollHeight can then accommodate the target offset (a
  // shrinking grid would otherwise clamp it), and the correction lands in the
  // same frame as the resize rather than one after it.
  watch(
    rowHeight,
    (next, previous) => {
      if (suppressNextRowHeightAnchor) {
        suppressNextRowHeightAnchor = false
        return
      }
      const container = gridContainer.value
      if (!container || !previous || next === previous) return
      const anchorIndex = indexAtOffset(container.scrollTop, previous)
      container.scrollTop = offsetForIndex(anchorIndex, next)
      scrollTop.value = container.scrollTop
    },
    { flush: "post" }
  )

  // --- Methods: Settings Persistence ---

  function serialize(): PersistedGridSettings {
    return {
      columnKeys: displayFields.value.map((col) => col.key),
      columnWidths: Object.fromEntries(
        displayFields.value.filter((col) => col.width).map((col) => [col.key, col.width as number])
      ),
      rowHeight: rowHeight.value,
      scrollTop: gridContainer.value?.scrollTop || 0,
      scrollLeft: gridContainer.value?.scrollLeft || 0,
      sortKeys: collection.sortKeys.value,
      groupKeys: collection.groupKeys.value,
      expandedGroups: Array.from(collection.expandedGroups.value),
    }
  }

  const persisted = useViewSettings({
    getKey: () => (settingsKey?.value ? `grid:${settingsKey.value}` : null),
    label: "grid",
    isLoaded,
    serialize,
    getListView: () => listView?.value,
    personalKeys: PERSONAL_KEYS,
    onWriteSuccess: () => {
      _saveErrorToastShown = false
    },
    onWriteError: () => {
      if (_saveErrorToastShown) return
      _saveErrorToastShown = true
      toast?.error?.("Could not save grid layout")
    },
  })

  function resetToDefaults() {
    displayFields.value = defaultColumns()
    restoreRowHeight(defaultRowHeight)
    isLoaded.value = true
  }

  function loadSettings() {
    // Reset the once-per-session toast flag so after recovering from a write
    // failure (e.g. the network came back) a future failure surfaces again.
    _saveErrorToastShown = false
    applySettings(parseGridSettings(persisted.read()))
  }

  function applySettings(settings: GridSettings | null) {
    if (!settings) {
      resetToDefaults()
      return
    }

    // Reconstruction — wrap in try-catch because stored settings can be stale
    // shapes from prior versions.
    try {
      if (settings.rowHeight) {
        restoreRowHeight(settings.rowHeight)
      }

      collection.sortKeys.value = settings.sortKeys || []

      // Grouping restores only when every stored entry is a { key } GroupKey;
      // anything else falls back to ungrouped, and the next save rewrites it.
      collection.groupKeys.value = settings.groupKeys || []

      if (settings.expandedGroups) {
        collection.expandedGroups.value = new Set(settings.expandedGroups)
      }

      if (settings.columnKeys) {
        const storedKeys = settings.columnKeys
        const reconstructed: Column[] = []
        const unresolvedKeys: string[] = []
        for (const key of storedKeys) {
          const column = dataColumns.value.find((col) => col.key === key)
          if (column) {
            reconstructed.push({
              ...column,
              width: settings.columnWidths?.[key] ?? column.width,
            })
          } else {
            unresolvedKeys.push(key)
          }
        }

        // Track unresolved keys so the dataColumns watcher can resolve them later
        pendingColumnKeys =
          unresolvedKeys.length > 0 ? { keys: storedKeys, widths: settings.columnWidths } : null

        // Safety check: ensure at least one data column
        if (reconstructed.length === 0) {
          const firstDataCol = dataColumns.value[0]
          if (firstDataCol) {
            reconstructed.push(firstDataCol)
          }
        }

        displayFields.value = reconstructed
      } else {
        displayFields.value = defaultColumns()
      }

      isLoaded.value = true

      // Restore scroll position after DOM renders
      if (settings.scrollTop || settings.scrollLeft) {
        nextTick(() => {
          if (gridContainer.value) {
            gridContainer.value.scrollTop = settings.scrollTop || 0
            gridContainer.value.scrollLeft = settings.scrollLeft || 0
          }
        })
      }
    } catch (error) {
      console.error("Failed to reconstruct grid settings, falling back to defaults:", error)
      toast?.error?.("Grid settings could not be restored and have been reset")
      persisted.remove()
      resetToDefaults()
    }
  }

  const saveSettings = persisted.save

  function resetSettings() {
    persisted.remove()
    // The reassignments below re-trigger the deep save watcher, which would
    // otherwise immediately re-save an entry behind remove()'s delete; skip
    // its very next invocation.
    suppressNextSave = true
    displayFields.value = defaultColumns()
    restoreRowHeight(defaultRowHeight)
    collection.resetState()
    // With the user's entry gone, the list view (if any) is what remains.
    const fallback = parseGridSettings(persisted.peek())
    if (fallback) applySettings(fallback)
  }

  // Drop this view's saved settings without applying anything — for a view
  // that isn't on screen; it loads fresh when next shown.
  function clearSettings() {
    persisted.remove()
  }

  // --- Methods: Grid Actions ---

  function handleScroll(event: Event) {
    const target = event.target as HTMLElement | null
    if (!target) {
      console.warn("[grid] handleScroll fired with no event target; ignoring")
      return
    }
    scrollTop.value = target.scrollTop
  }

  function updateContainerHeight() {
    if (gridContainer.value) {
      containerHeightPx.value = gridContainer.value.clientHeight
    }
  }

  function handleColumnResize({ columnKey, width }: { columnKey: string; width: number }) {
    const index = displayFields.value.findIndex((col) => col.key === columnKey)
    if (index !== -1) {
      const newCols = [...displayFields.value]
      newCols[index] = { ...newCols[index]!, width }
      displayFields.value = newCols
    }
  }

  function handleColumnReorder({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }) {
    const newCols = [...displayFields.value]
    const [moved] = newCols.splice(fromIndex, 1)
    if (!moved) {
      console.warn(
        `[useGrid] handleColumnReorder fromIndex=${fromIndex} out of range (cols=${displayFields.value.length})`
      )
      return
    }
    newCols.splice(toIndex, 0, moved)
    displayFields.value = newCols
  }

  // --- Watchers ---

  // Skip the empty pre-load state.
  watch(
    displayFields,
    (cols) => {
      if (cols.length) collection.setSortedFields(cols)
    },
    { immediate: true }
  )

  watch(
    [
      displayFields,
      rowHeight,
      () => collection.sortKeys.value,
      () => collection.groupKeys.value,
      () => collection.expandedGroups.value,
    ],
    () => {
      if (suppressNextSave) {
        suppressNextSave = false
        return
      }
      saveSettings()
    },
    { deep: true }
  )

  // Settings load while the grid is on screen, and again for a new key.
  // Off screen it's unloaded, so it never saves the shared collection's sort
  // and grouping as its own.
  watch(
    () => [toValue(active), settingsKey?.value] as const,
    ([on]) => {
      isLoaded.value = false
      if (on) loadSettings()
    },
    { immediate: true }
  )

  // Send any pending write when the owning scope tears down.
  onScopeDispose(() => {
    persisted.save.flush()
  })

  watch(dataColumns, (newCols, oldCols) => {
    if (!isLoaded.value) return

    // If loadSettings couldn't resolve all stored keys (schema wasn't loaded yet),
    // retry reconstruction now that more fields are available.
    if (pendingColumnKeys) {
      const { keys, widths } = pendingColumnKeys
      const reconstructed: Column[] = []
      for (const key of keys) {
        const column = newCols.find((col) => col.key === key)
        if (!column) continue
        reconstructed.push({
          ...column,
          width: widths?.[key] ?? column.width,
        })
      }

      if (reconstructed.length > 0) {
        const currentKeys = new Set(displayFields.value.map((c) => c.key))
        const newlyResolved = reconstructed.filter((c) => !currentKeys.has(c.key))
        if (newlyResolved.length > 0) {
          displayFields.value = reconstructed
        }
        const stillUnresolved = keys.filter((k) => !newCols.find((c) => c.key === k))
        if (stillUnresolved.length === 0) pendingColumnKeys = null
      }
      return
    }

    // Refresh existing columns with current field metadata (schema renames, enum values, etc.)
    // while preserving user-customized properties (width).
    const newColsByKey = new Map(newCols.map((c) => [c.key, c]))
    let updated = false
    const refreshed = displayFields.value.map((col) => {
      const fresh = newColsByKey.get(col.key)
      if (!fresh) return col
      // Preserve user-set width, merge everything else from the fresh definition
      const merged: Column = { ...fresh, width: col.width ?? fresh.width }
      if (JSON.stringify(merged) !== JSON.stringify(col)) updated = true
      return merged
    })

    // Also add genuinely new fields (schema superset grew)
    const currentKeys = new Set(displayFields.value.map((c) => c.key))
    const oldKeys = new Set((oldCols || []).map((c) => c.key))
    const added = newCols.filter((c) => !oldKeys.has(c.key) && !currentKeys.has(c.key))

    if (updated || added.length > 0) {
      displayFields.value = [...refreshed, ...added]
    }
  })

  return {
    // Refs
    gridContainer,

    // Whether the selection gutter renders.
    showRowSelect,

    // Grid State
    displayFields,
    rowHeight,
    groupHeaderHeight,
    scrollTop,
    isLoaded,

    // Computed: Columns
    enhancedColumns,

    // Computed: Virtual Scrolling
    groupedRows,
    totalHeight,
    visibleIndices,
    visibleItems,
    visibleItemsOffset,

    // Methods: Settings
    loadSettings,
    saveSettings,
    resetSettings,
    sharedSettings: persisted.shared,
    clearSettings,

    // Methods: Grid Actions
    handleScroll,
    updateContainerHeight,
    handleColumnResize,
    handleColumnReorder,
  }
}
